import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiService } from './api.service';
import { TemplateStructure, OcrData, LineItem } from '../models/ocr.models';

@Injectable({
    providedIn: 'root'
})
export class OcrApiService {
    private readonly API_BASE = '/api/pr-template';

    constructor(private apiService: ApiService) { }

    discoverStructure(taxReturnType: string = 'corporate'): Observable<TemplateStructure> {
        return this.apiService.send('Get', `${this.API_BASE}/discover_structure`, {
            tax_return_type: taxReturnType
        });
    }

    loadOcrData(year: string, language: string, document: string, page: number, taxReturnType: string = 'corporate'): Observable<OcrData> {
        return this.apiService.send('Get', `${this.API_BASE}/load_ocr_data`, {
            year,
            language,
            document,
            page,
            tax_return_type: taxReturnType
        });
    }

    loadImage(year: string, language: string, document: string, page: number, taxReturnType: string = 'corporate'): Observable<Blob> {
        return this.apiService.send('Get', `${this.API_BASE}/load_page_image`, {
            year,
            language,
            document,
            page,
            tax_return_type: taxReturnType
        }).pipe(
            map(response => {
                // Convert base64 to Blob
                const byteCharacters = atob(response.image_base64);
                const byteNumbers = new Array(byteCharacters.length);
                for (let i = 0; i < byteCharacters.length; i++) {
                    byteNumbers[i] = byteCharacters.charCodeAt(i);
                }
                const byteArray = new Uint8Array(byteNumbers);
                return new Blob([byteArray], { type: 'image/png' });
            })
        );
    }

    loadLineItems(year: string, document: string, language: string, page: number, taxReturnType: string = 'corporate'): Observable<LineItem[]> {
        return this.apiService.send('Get', `${this.API_BASE}/load_line_items`, {
            year,
            document,
            language,
            page,
            tax_return_type: taxReturnType
        });
    }

    saveLineItem(
        year: string,
        document: string,
        language: string,
        lineItemData: any,
        lineItemName: string,
        taxReturnType: string = 'corporate'
    ): Observable<any> {
        return this.apiService.send('Post', `${this.API_BASE}/save_line_item`, {
            year,
            document,
            language,
            line_item_data: lineItemData,
            line_item_name: lineItemName,
            tax_return_type: taxReturnType
        });
    }

    deleteLineItem(
        year: string,
        document: string,
        language: string,
        page: number,
        lineItemId: string,
        taxReturnType: string = 'corporate'
    ): Observable<any> {
        return this.apiService.send('Post', `${this.API_BASE}/delete_line_item`, {
            year,
            document,
            language,
            page,
            line_item_id: lineItemId,
            tax_return_type: taxReturnType
        });
    }

    generateOCR(year: string, document: string, language: string, page: number, taxReturnType: string = 'corporate'): Observable<any> {
        return this.apiService.send('Post', `${this.API_BASE}/generate_ocr`, {
            year,
            document,
            language,
            page,
            tax_return_type: taxReturnType
        });
    }

    convertPdfToImage(
        year: string,
        document: string,
        language: string,
        page: number,
        pdfPath: string,
        outputPath: string,
        taxReturnType: string = 'corporate'
    ): Observable<any> {
        return this.apiService.send('Post', `${this.API_BASE}/convert_pdf_to_image`, {
            year,
            document,
            language,
            page,
            pdf_path: pdfPath,
            output_path: outputPath,
            tax_return_type: taxReturnType
        });
    }
}